import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AppContext';
import {
  Eye, Heart, Video as VideoIcon,
  Loader2, Upload, BarChart3, Award, Users, X
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const resolveAvatar = (a) => {
  if (!a) return null;
  if (a.startsWith('http')) return a;
  return `${API}/api/files/${a}`;
};

const formatNum = (n) => {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return v.toLocaleString();
};

const StatTile = ({ icon: Icon, label, value, sub, color = 'orange' }) => (
  <div
    className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-5"
    data-testid={`artist-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="flex items-center justify-between mb-3">
      <div className={`w-10 h-10 rounded-lg bg-${color}-500/15 flex items-center justify-center`}>
        <Icon className={`w-5 h-5 text-${color}-400`} />
      </div>
    </div>
    <p className="text-2xl font-bold text-white">{value}</p>
    <p className="text-sm text-zinc-500">{label}</p>
    {sub && <p className="text-xs text-zinc-600 mt-1">{sub}</p>}
  </div>
);

export const ArtistAnalyticsPage = () => {
  const { axiosInstance, user } = useAuth();
  const navigate = useNavigate();
  // showLikers: { video, likers, loading } | null — drives the modal that
  // lists the actual fans who liked a given video. Likers are auth-only
  // (anonymous browsers can't like), so this is a real fan list.
  const [showLikers, setShowLikers] = useState(null);

  const openLikers = useCallback(async (video) => {
    setShowLikers({ video, likers: [], loading: true });
    try {
      const res = await axiosInstance.get(`/videos/${video.id}/likers?limit=200`);
      setShowLikers({ video, likers: res.data?.likers || [], loading: false });
    } catch (err) {
      setShowLikers({ video, likers: [], loading: false, error: err?.response?.data?.detail || 'Failed to load likers' });
    }
  }, [axiosInstance]);
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async (rangeDays) => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/creator/analytics?days=${rangeDays}`);
      setData(res.data);
    } catch (err) {
      console.error('Failed to fetch artist analytics:', err);
      setData({ period_days: rangeDays, total_videos: 0, video_analytics: [], daily_earnings: [] });
    } finally {
      setLoading(false);
    }
  }, [axiosInstance]);

  useEffect(() => {
    fetchData(days);
  }, [days, fetchData]);

  const videos = data?.video_analytics || [];
  const totalViews = videos.reduce((s, v) => s + (v.views || 0), 0);
  const totalLikes = videos.reduce((s, v) => s + (v.likes || 0), 0);
  const topVideo = videos[0];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black px-5 md:px-8 pt-6 pb-32 max-w-6xl mx-auto" data-testid="artist-analytics-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-white tracking-tight">Your Analytics</h1>
          <p className="text-zinc-400 mt-1">Performance across your videos for the last {days} days</p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                days === d ? 'bg-orange-500 text-white' : 'bg-zinc-800/70 text-zinc-300 hover:bg-zinc-800'
              }`}
              data-testid={`range-${d}d`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* Empty state */}
      {videos.length === 0 ? (
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-10 text-center">
          <BarChart3 className="w-14 h-14 text-zinc-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">No analytics yet</h2>
          <p className="text-zinc-500 mb-6">Upload your first video to start tracking views, likes and watch time.</p>
          <button
            onClick={() => navigate('/studio')}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-black font-semibold hover:bg-zinc-200 transition-colors"
            data-testid="artist-empty-upload-btn"
          >
            <Upload className="w-4 h-4" />
            Upload to Studio
          </button>
        </div>
      ) : (
        <>
          {/* Top stat tiles — simplified to the two metrics creators actually care about */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <StatTile icon={Eye} label="Total Views" value={formatNum(totalViews)} color="cyan" />
            <StatTile icon={Heart} label="Total Likes" value={formatNum(totalLikes)} color="pink" />
          </div>

          {/* Top performer card */}
          {topVideo && (
            <div
              className="rounded-2xl bg-gradient-to-br from-orange-500/10 via-purple-500/10 to-zinc-900/40 border border-zinc-800 p-5 mb-6 flex items-center gap-4 cursor-pointer hover:border-orange-500/50 transition-colors"
              onClick={() => navigate(`/video/${topVideo.id}`)}
              data-testid="artist-top-performer"
            >
              <div className="w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center flex-shrink-0">
                <Award className="w-6 h-6 text-orange-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs uppercase tracking-wider text-orange-400 mb-0.5">Top performer</p>
                <p className="font-semibold text-white truncate">{topVideo.title}</p>
                <p className="text-sm text-zinc-400 mt-0.5">
                  {formatNum(topVideo.views)} views · {formatNum(topVideo.likes)} likes · {topVideo.watch_time_hours.toFixed(1)}h watched
                </p>
              </div>
            </div>
          )}

          {/* Per-video performance table */}
          <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 overflow-hidden mb-6">
            <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between">
              <h3 className="font-semibold text-white">Video Performance</h3>
              <span className="text-xs text-zinc-500">{data?.total_videos || videos.length} videos</span>
            </div>
            <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-2 border-b border-zinc-800 text-xs uppercase tracking-wider text-zinc-500">
              <div className="col-span-5">Title</div>
              <div className="col-span-2 text-right">Views</div>
              <div className="col-span-2 text-right">Likes</div>
              <div className="col-span-2 text-right">Watch hrs</div>
              <div className="col-span-1 text-right">Unique</div>
            </div>
            <div className="divide-y divide-zinc-800">
              {videos.map((v) => (
                <div
                  key={v.id}
                  className="grid grid-cols-12 gap-3 px-5 py-3 hover:bg-zinc-800/30 items-center"
                  data-testid={`artist-video-row-${v.id}`}
                >
                  <div
                    className="col-span-12 md:col-span-5 flex items-center gap-3 min-w-0 cursor-pointer"
                    onClick={() => navigate(`/video/${v.id}`)}
                  >
                    <div className="w-9 h-9 rounded-lg bg-zinc-800 flex items-center justify-center flex-shrink-0">
                      <VideoIcon className="w-4 h-4 text-zinc-400" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-white truncate">{v.title}</p>
                    </div>
                  </div>
                  <div
                    className="col-span-3 md:col-span-2 text-right text-sm text-white cursor-pointer"
                    onClick={() => navigate(`/video/${v.id}`)}
                  >
                    {formatNum(v.views)}
                  </div>
                  {/* Clickable Likes — opens modal with actual likers */}
                  <div className="col-span-3 md:col-span-2 text-right text-sm">
                    {(v.likes || 0) > 0 ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); openLikers(v); }}
                        className="inline-flex items-center gap-1 text-pink-300 hover:text-pink-200 hover:underline"
                        data-testid={`artist-likes-btn-${v.id}`}
                      >
                        {formatNum(v.likes)}
                        <Users className="w-3.5 h-3.5 opacity-70" />
                      </button>
                    ) : (
                      <span className="text-zinc-500">{formatNum(v.likes)}</span>
                    )}
                  </div>
                  <div
                    className="col-span-3 md:col-span-2 text-right text-sm text-white cursor-pointer"
                    onClick={() => navigate(`/video/${v.id}`)}
                  >
                    {v.watch_time_hours.toFixed(1)}
                  </div>
                  <div
                    className="col-span-3 md:col-span-1 text-right text-sm text-white cursor-pointer"
                    onClick={() => navigate(`/video/${v.id}`)}
                  >
                    {v.unique_viewers}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Likers modal — shows the real users who liked a given video.
          Anonymous viewers can't like, so this is always a list of registered fans. */}
      {showLikers && (
        <div
          className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowLikers(null)}
          data-testid="likers-modal"
        >
          <div
            className="relative w-full max-w-md bg-zinc-900 rounded-2xl border border-pink-500/30 overflow-hidden flex flex-col max-h-[80vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800 flex-shrink-0">
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-wider text-pink-400">Liked by</p>
                <p className="font-semibold text-white truncate">{showLikers.video.title}</p>
              </div>
              <button
                onClick={() => setShowLikers(null)}
                className="p-1.5 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 flex-shrink-0"
                data-testid="likers-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="overflow-y-auto flex-1">
              {showLikers.loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-pink-400" />
                </div>
              ) : showLikers.error ? (
                <p className="text-center text-sm text-red-400 py-12 px-5">{showLikers.error}</p>
              ) : showLikers.likers.length === 0 ? (
                <p className="text-center text-sm text-zinc-500 py-12 px-5">
                  No likers yet. Likes from anonymous viewers don&apos;t count — only signed-in users.
                </p>
              ) : (
                <ul className="divide-y divide-zinc-800">
                  {showLikers.likers.map((u) => (
                    <li
                      key={u.id}
                      className="flex items-center gap-3 px-5 py-3 hover:bg-zinc-800/40 cursor-pointer"
                      onClick={() => { setShowLikers(null); navigate(`/creator/${u.username || u.id}`); }}
                      data-testid={`liker-row-${u.username}`}
                    >
                      {u.avatar ? (
                        <img src={resolveAvatar(u.avatar)} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                          {(u.display_name || u.username || '?').charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-white truncate">{u.display_name}</p>
                        <p className="text-xs text-zinc-500 truncate">@{u.username}{u.role === 'artist' ? ' · Artist' : ''}</p>
                      </div>
                      <Heart className="w-4 h-4 text-pink-400 flex-shrink-0" />
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {!showLikers.loading && !showLikers.error && showLikers.likers.length > 0 && (
              <div className="px-5 py-3 border-t border-zinc-800 text-xs text-zinc-500 text-center flex-shrink-0">
                {showLikers.likers.length} fan{showLikers.likers.length !== 1 ? 's' : ''} · click a row to view their profile
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
